import React from "react";
import { AppBar, Badge, Box, Button, Container, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { useShopCart } from "../shopCart";

export default function ShopLayout() {
  const location = useLocation();
  const cartItems = useShopCart();
  const cartCount = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const accent = "#e86026";
  const accentDeep = "#c94b19";
  const support = "#656e8f";
  const shell = "#fff7f2";
  const footerItems = [
    { label: "Accueil", icon: <HomeRoundedIcon sx={{ fontSize: 20 }} />, to: "/shop" },
    { label: "Chat", icon: <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20 }} />, to: "/shop" },
    { label: "Favoris", icon: <FavoriteBorderRoundedIcon sx={{ fontSize: 20 }} />, to: "/shop" },
    { label: "Panier", icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 20 }} />, to: "/shop/cart" },
    { label: "Compte", icon: <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />, to: "/login" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          `radial-gradient(circle at top left, rgba(232,96,38,0.18), transparent 24%), radial-gradient(circle at top right, rgba(101,110,143,0.16), transparent 20%), linear-gradient(180deg, ${shell} 0%, #f6f8fc 100%)`,
      }}
    >
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          display: "flex",
          backdropFilter: "blur(20px)",
          bgcolor: "rgba(255,247,242,0.88)",
          borderBottom: `1px solid rgba(232,96,38,0.16)`,
          boxShadow: "0 12px 32px rgba(101,110,143,0.08)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: "68px !important", md: "74px !important" }, py: 0.75 }}>
          <Container maxWidth="xl" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Stack
              component={RouterLink}
              to="/shop"
              direction="row"
              spacing={1.25}
              sx={{ textDecoration: "none", color: "#142033", alignItems: "center", minWidth: 0 }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Gada"
                sx={{
                  width: { xs: 60, md: 72 },
                  height: { xs: 60, md: 72 },
                  borderRadius: "20px",
                  objectFit: "cover",
                  border: "2px solid rgba(255,255,255,0.9)",
                  boxShadow: "0 16px 34px rgba(232,96,38,0.22)",
                  flexShrink: 0,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
              <IconButton
                component={RouterLink}
                to="/shop/cart"
                aria-label="Panier"
                sx={{
                  width: 48,
                  height: 48,
                  color: accent,
                  border: `1px solid rgba(232,96,38,0.22)`,
                  borderRadius: "16px",
                  bgcolor: "rgba(255,255,255,0.92)",
                  boxShadow: "0 10px 22px rgba(232,96,38,0.12)",
                  transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                  "&:hover": {
                    bgcolor: "#fff",
                    transform: "translateY(-1px)",
                    boxShadow: "0 14px 26px rgba(232,96,38,0.18)",
                  },
                }}
              >
                <Badge
                  badgeContent={cartCount}
                  invisible={!cartCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: accent,
                      color: "#fff",
                      fontWeight: 800,
                      boxShadow: "0 4px 10px rgba(232,96,38,0.3)",
                    },
                  }}
                >
                  <ShoppingBagOutlinedIcon />
                </Badge>
              </IconButton>
              <IconButton
                component={RouterLink}
                to="/login"
                aria-label="Login"
                sx={{
                  width: 48,
                  height: 48,
                  color: "#fff",
                  bgcolor: support,
                  border: `1px solid ${support}`,
                  borderRadius: "16px",
                  boxShadow: "0 12px 26px rgba(101,110,143,0.22)",
                  transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                  "&:hover": {
                    bgcolor: accentDeep,
                    borderColor: accentDeep,
                    transform: "translateY(-1px)",
                    boxShadow: "0 14px 30px rgba(201,75,25,0.22)",
                  },
                }}
              >
                <LoginRoundedIcon />
              </IconButton>
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 }, pb: { xs: 14, md: 16 } }}>
        <Outlet />
      </Container>

      <Box
        sx={{
          position: "fixed",
          left: "50%",
          bottom: { xs: 12, md: 18 },
          transform: "translateX(-50%)",
          width: "min(430px, calc(100vw - 20px))",
          px: 1,
          py: 1.1,
          borderRadius: "22px",
          bgcolor: "rgba(255,255,255,0.96)",
          border: "1px solid #f0ddd3",
          boxShadow: "0 18px 36px rgba(101,110,143,0.16)",
          backdropFilter: "blur(18px)",
          zIndex: 1200,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {footerItems.map((item) => {
            const active =
              item.to === "/shop"
                ? location.pathname === "/shop"
                : location.pathname.startsWith(item.to);

            return (
              <Button
                key={item.label}
                component={RouterLink}
                to={item.to}
                sx={{
                  minWidth: 0,
                  px: 0.6,
                  py: 0.35,
                  display: "grid",
                  gap: 0.35,
                  color: active ? accent : support,
                  textTransform: "none",
                }}
              >
                {item.icon}
                <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>{item.label}</Typography>
              </Button>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
